import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useIsTruncated } from 'hooks/useIsTruncated';
import CustomForwardIcon from './CustomForwardIcon';
import { formatPath } from './utils';
import IcoVariablesFolder from 'components/icon/icons/IcoVariablesFolder';
import { CopyButton } from './CopyButton';

const BoxStyled = styled(Box)(({ isRounded }: { isRounded: boolean }) => ({
  display: 'flex',
  padding: isRounded ? '8px 6px 8px 6px' : '8px 16px 8px 16px',
  alignItems: 'center',
  width: '100%',
  cursor: 'pointer',
  fontWeight: 'bold',
  borderRadius: isRounded ? '8px' : 0,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    '.copyBtn': {
      opacity: 1,
      maxWidth: '60px',
      backgroundColor: 'transparent',
    },
  },
}));

const IconFolderStyled = styled(IcoVariablesFolder)(() => ({
  cursor: 'pointer',
  width: '20px',
  height: '20px',
  verticalAlign: 'bottom',
  marginRight: '6px',
  '& path': {
    fill: '#00283A',
  },
}));
const TypographyStyled = styled(Typography)(() => ({
  lineHeight: '20px',
  color: 'rgba(0, 40, 58, 0.87)',
}));

type SummaryTitleWithTooltipProps = {
  group: string;
  expanded: boolean;
  onActiveUpdate: () => void;
  isRounded?: boolean;
  onCopy?: () => Promise<boolean>;
  isArray?: boolean;
  isNested?: boolean;
};

const SummaryTitleWithTooltip = ({
  group,
  expanded,
  onActiveUpdate,
  isRounded = true,
  onCopy,
  isArray = false,
  isNested = false,
}: SummaryTitleWithTooltipProps) => {
  const { t } = useTranslation();
  const { ref, isTruncated } = useIsTruncated([group]);
  const [isCopied, setIsCopied] = useState(false);
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const parts = group.indexOf('/') !== -1 ? group.split('/') : [group];
  const formatedPath = formatPath(parts);
  const isTooltipActive = /(\.{3}|…)/.test(formatedPath) || isTruncated;

  useEffect(() => () => clearTimeout(copyResetTimeoutRef.current), []);

  const handleCopy = async () => {
    if (!onCopy) return;
    const success = await onCopy();
    if (success) {
      setIsCopied(true);
      copyResetTimeoutRef.current = setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }
  };

  return (
    <Tooltip
      slotProps={{
        popper: {
          sx: { zIndex: 'tooltip' },
          placement: 'auto',
          disablePortal: true,
        },
      }}
      title={isTooltipActive ? group : ''}
      enterDelay={700}
      disableHoverListener={!isTooltipActive}
    >
      <BoxStyled isRounded={isRounded} onClick={onActiveUpdate}>
        <TypographyStyled ref={ref} variant="body2" noWrap>
          <IconFolderStyled />
          {formatedPath}
        </TypographyStyled>
        {(isArray || isNested) && (
          <Chip
            data-testid={isNested ? 'folder-nested-badge' : 'folder-array-badge'}
            label={t(isNested ? 'editor.variables.nested_badge' : 'editor.variables.array_badge')}
            size="small"
            variant="outlined"
            component="span"
            color={isNested ? 'info' : 'default'}
            sx={{ flexShrink: 0, ml: 0.5 }}
          />
        )}
        <CustomForwardIcon expanded={expanded} />
        {onCopy && (
          <CopyButton
            data-testid="copy-folder-btn"
            isCopied={isCopied}
            copyLabel={t('editor.variables.copy')}
            copiedLabel={t('editor.variables.copied')}
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
          />
        )}
      </BoxStyled>
    </Tooltip>
  );
};

export default SummaryTitleWithTooltip;
